import React, { useEffect, useState } from 'react';
import './DoorLocksList.scss';

const PANEL_WIDTH = 720;

interface LockRow {
  refrId: number;
  cellOrWorldDesc: string;
  summary: string;
  config: {
    kind: 'faction' | 'person';
    everyoneCanClose: boolean;
    code?: string;
  };
}

interface ShowEventDetail {
  locks: LockRow[];
}

const DoorLocksList: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [locks, setLocks] = useState<LockRow[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as ShowEventDetail;
      setLocks(detail.locks ?? []);
      setSearch('');
      setVisible(true);
    };
    window.addEventListener('skymp5-client:showDoorLocksList', handler);
    return () => window.removeEventListener('skymp5-client:showDoorLocksList', handler);
  }, []);

  const send = (message: string) => {
    if ((window as any).skyrimPlatform?.sendMessage) {
      (window as any).skyrimPlatform.sendMessage(message);
    }
  };

  const close = () => setVisible(false);

  const edit = (refrId: number) => {
    // Close the list first - the config panel is a separate overlay and
    // would otherwise render on top of/behind this one simultaneously.
    close();
    send('door-lock-edit:' + JSON.stringify({ refrId }));
  };

  const teleport = (refrId: number) => {
    send('door-lock-teleport:' + JSON.stringify({ refrId }));
  };

  const remove = (refrId: number) => {
    setLocks((prev) => prev.filter((l) => l.refrId !== refrId));
    send('door-lock-list-remove:' + JSON.stringify({ refrId }));
  };

  const q = search.trim().toLowerCase();
  const filtered = locks.filter((l) =>
    !q ||
    l.summary.toLowerCase().includes(q) ||
    l.cellOrWorldDesc.toLowerCase().includes(q) ||
    String(l.refrId).includes(q)
  );

  if (!visible) return null;

  return (
    <div className="door-locks-overlay">
      <div className="door-locks-panel skyrim-panel" style={{ width: `${PANEL_WIDTH}px` }}>
        <div className="door-locks-header">
          <div className="door-locks-header-title">Verrous configurés ({locks.length})</div>
          <button type="button" className="door-locks-header-close" onClick={close} aria-label="Fermer">×</button>
        </div>

        <input
          className="door-locks-search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filtrer par faction, personne, lieu..."
        />

        <div className="door-locks-rows">
          {filtered.length === 0 && (
            <div className="door-locks-empty">Aucun verrou{locks.length > 0 ? ' ne correspond au filtre' : " n'est configuré"}.</div>
          )}
          {filtered.map((l) => (
            <div className="door-locks-row" key={l.refrId}>
              <div className="door-locks-row__info">
                <span className="door-locks-row__summary">{l.summary}</span>
                <span className="door-locks-row__meta">
                  #{l.refrId}{l.cellOrWorldDesc ? ` - ${l.cellOrWorldDesc}` : ''}
                  {!l.config.everyoneCanClose ? ' - fermeture restreinte' : ''}
                </span>
              </div>
              <div className="door-locks-row__actions">
                <button type="button" className="door-locks-btn" onClick={() => teleport(l.refrId)}>Téléporter</button>
                <button type="button" className="door-locks-btn" onClick={() => edit(l.refrId)}>Modifier</button>
                <button type="button" className="door-locks-btn door-locks-btn--danger" onClick={() => remove(l.refrId)}>Supprimer</button>
              </div>
            </div>
          ))}
        </div>

        <div className="door-locks-actions">
          <button type="button" className="door-locks-btn door-locks-btn--ghost" onClick={close}>Fermer</button>
        </div>
      </div>
    </div>
  );
};

export default DoorLocksList;
